import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Incentives and Rewards (BACKLOG C-16 S1, GLE-8408). Team → Incentives is
 * the LGA/Admin builder (behind `user_roles_incentives_enabled` and manage/me's
 * `features.incentives`); Rewards is every caller's page (behind
 * `rewards_enabled`). Off, every route answers 404 with error_code 1035. Money
 * is integer cents; rule documents are Command Center's JSON, kept as is.
 */

export const INCENTIVE_STATUSES = {
  DRAFT: "draft",
  ACTIVE: "active",
  PAUSED: "paused",
  ARCHIVED: "archived",
} as const;

export type IncentiveStatusT =
  (typeof INCENTIVE_STATUSES)[keyof typeof INCENTIVE_STATUSES];

export const INCENTIVE_CADENCES = {
  ONCE: "once",
  WEEKLY: "weekly",
  DAILY: "daily",
  LIVE_RACE: "live_race",
} as const;

export type IncentiveCadenceT =
  (typeof INCENTIVE_CADENCES)[keyof typeof INCENTIVE_CADENCES];

export const PAYOUT_TYPES = {
  FLAT_PER_REP: "flat_per_rep",
  FLAT_PER_MEETING: "flat_per_meeting",
  PCT_GMV: "pct_gmv",
  PCT_USER_PAYOUT: "pct_user_payout",
  PCT_TOTAL_PAYOUT: "pct_total_payout",
  THRESHOLD_TIERED: "threshold_tiered",
  FLAT_WINNER: "flat_winner",
  RANKED_TIERED: "ranked_tiered",
  FLAT_PER_QUALIFIER: "flat_per_qualifier",
  GROWTH_TIERED: "growth_tiered",
  TEAM_TIERED: "team_tiered",
} as const;

export type PayoutTypeT = (typeof PAYOUT_TYPES)[keyof typeof PAYOUT_TYPES];

export const FUNDED_BY = {
  LGA: "lga",
  CUSTOMER: "customer",
  GLENCOCO: "glencoco",
} as const;

export type FundedByT = (typeof FUNDED_BY)[keyof typeof FUNDED_BY];

export type IncentiveSegmentT = "upcoming" | "active" | "past";

export const AGGREGATORS = {
  SUM_TOTAL_PAYOUT: "sum_actual_total_payout",
  SUM_USER_PAYOUT: "sum_actual_user_payout",
  COUNT_MEETINGS: "count_meetings",
  SHOW_RATE: "show_rate",
  BOOKED_COUNT: "booked_count",
} as const;

export type AggregatorT = (typeof AGGREGATORS)[keyof typeof AGGREGATORS];

export interface IncentiveFilterI {
  field: string;
  op: string;
  value: unknown;
}

export interface IncentiveRuleI {
  filters?: Array<IncentiveFilterI>;
  aggregator?: AggregatorT;
}

export interface IncentiveTriggerI {
  show_rate_min?: number;
  booked_min?: number;
  qualifying_count_min?: number;
  qualifying_value_min?: number;
  date_start?: string;
  date_end?: string;
  booked_start?: string;
  booked_end?: string;
  workspace_total_min?: { metric: string; value: number } | null;
  growth_tiers?: Array<{ multiplier: number; amount: number }>;
  prior_period_min_value?: number;
  team_tiers?: Array<{ threshold: number; amount: number }>;
  team_metric?: "qualifying_count" | "user_payout";
  paid_only?: boolean;
  milestones?: Array<{ threshold: number; label: string }>;
}

/** A place prize (ranked) or a bonus tier (threshold), in dollars. */
export interface IncentiveTierI {
  place?: number;
  threshold?: number;
  amount: number;
}

export interface IncentiveRankI {
  aggregator?: AggregatorT;
  top_n?: number;
  tiebreaker?: AggregatorT | null;
}

export interface IncentiveRaceI {
  metric?: "qualifying_count" | "booked_count";
  target_count?: number;
}

/** What the builder saves. */
export interface IncentiveInputI {
  name: string;
  description?: string | null;
  notes?: string | null;
  status: IncentiveStatusT;
  rep_visible?: boolean;
  workspace_ids: Array<string>;
  campaign_ids: Array<string>;
  excluded_campaign_ids: Array<string>;
  participant_user_ids: Array<string>;
  excluded_user_ids: Array<string>;
  beneficiary_user_ids: Array<string>;
  start_at: string | null;
  end_at: string | null;
  cadence: IncentiveCadenceT;
  payout_type: PayoutTypeT;
  /** Dollars (flat types) or percent (% types). */
  payout_value: number;
  rule: IncentiveRuleI;
  trigger: IncentiveTriggerI;
  tiers: Array<IncentiveTierI>;
  rank: IncentiveRankI;
  race: IncentiveRaceI;
  funded_by: FundedByT;
  announce_days?: number;
}

export interface IncentiveI extends IncentiveInputI {
  id: string;
  rep_visible: boolean;
  announce_days: number;
  source: "app" | "command_center";
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  segment: IncentiveSegmentT;
}

export interface IncentivePeriodI {
  start: string;
  end: string;
  completed: boolean;
}

export interface IncentiveSummaryI extends IncentiveI {
  period: IncentivePeriodI | null;
  owed_cents: number;
  qualifying_reps: number;
  participants: number;
  qualifying_meetings: number;
}

export interface IncentiveTierCentsI {
  place?: number;
  threshold?: number;
  amount_cents: number;
}

export interface IncentiveMeetingI {
  id: string;
  campaign_name: string;
  booked_at: string;
  meeting_date: string | null;
  status: string;
  rep_pay_cents: number;
  potential_cents: number;
  paid: boolean;
}

/** One rep in a period. `email` and other reps' `meetings` are managers' only. */
export interface IncentiveStandingI {
  user_id: string;
  name: string;
  email?: string;
  is_viewer?: boolean;
  rank: number;
  qualifying_count: number;
  booked_count: number;
  show_count: number;
  show_rate: number;
  qualifying_value_cents: number;
  qualifying_user_payout_cents: number;
  amount_cents: number;
  earned_cents: number;
  potential_cents: number;
  prior_earnings_cents: number;
  growth_ratio: number | null;
  team_qualifying_total: number;
  team_shadow_total: number;
  team_user_payout_cents: number;
  trigger_met: boolean;
  tier: IncentiveTierCentsI | null;
  next_tier: IncentiveTierCentsI | null;
  meetings?: Array<IncentiveMeetingI>;
}

export interface IncentiveBeneficiarySourceI {
  user_id: string;
  name: string;
  amount_cents: number;
}

export interface IncentiveBeneficiaryI {
  user_id: string;
  name: string;
  amount_cents: number;
  sources: Array<IncentiveBeneficiarySourceI>;
}

export interface IncentiveAuditI {
  fetched: number;
  after_campaign: number;
  after_rep_filters: number;
  after_rule_filters: number;
  dropped_excluded: Array<string> | null;
  dropped_not_allowed: Array<string> | null;
  dropped_by_rule: Array<string> | null;
}

export interface IncentiveStandingsI {
  incentive?: IncentiveI;
  periods: Array<IncentivePeriodI>;
  period: IncentivePeriodI | null;
  totals: {
    amount_cents: number;
    qualifying_meetings: number;
    qualifying_reps: number;
    participants: number;
  };
  reps: Array<IncentiveStandingI> | null;
  beneficiaries: Array<IncentiveBeneficiaryI> | null;
  audit?: IncentiveAuditI;
}

export interface IncentiveEarningLineI {
  incentive_id: string;
  incentive_name: string;
  payout_type: PayoutTypeT;
  funded_by: FundedByT;
  period_start: string;
  period_end: string;
  state: "settled" | "in_progress";
  workspace_id: string;
  rep_user_id: string;
  rep_name: string;
  rep_email: string;
  amount_cents: number;
  sources: Array<IncentiveBeneficiarySourceI> | null;
}

export interface IncentiveEarningsI {
  month: string;
  computed_at: string;
  lines: Array<IncentiveEarningLineI>;
}

/** An incentive as a caller sees it: no notes, funding or scope ids. */
export interface RewardI {
  id: string;
  name: string;
  description: string | null;
  status: IncentiveStatusT;
  segment: IncentiveSegmentT;
  start_at: string | null;
  end_at: string | null;
  cadence: IncentiveCadenceT;
  payout_type: PayoutTypeT;
  payout_value: number;
  rule: IncentiveRuleI;
  trigger: IncentiveTriggerI;
  tiers: Array<IncentiveTierI>;
  rank: IncentiveRankI;
  race: IncentiveRaceI;
  announce_until: string | null;
}

export interface RewardCardI {
  reward: RewardI;
  period: IncentivePeriodI | null;
  me: IncentiveStandingI | null;
  top: Array<IncentiveStandingI>;
  participants: number;
}

export interface RewardsI {
  active: Array<RewardCardI>;
  upcoming: Array<RewardCardI>;
  past: Array<RewardCardI>;
}

export interface RewardDetailI {
  reward?: RewardI;
  periods: Array<IncentivePeriodI>;
  period: IncentivePeriodI | null;
  reps: Array<IncentiveStandingI>;
  me: IncentiveStandingI | null;
}

export interface RewardAnnouncementsI {
  rewards: Array<RewardI>;
}

/** A campaign or rep the builder can scope to (`/incentives/options`). */
export interface IncentiveOptionI {
  id: string;
  name: string;
  email?: string;
}

export interface IncentiveOptionsI {
  campaigns: Array<IncentiveOptionI>;
  reps: Array<IncentiveOptionI>;
}

export interface IncentiveResponseI {
  incentive: IncentiveI;
}

export interface IncentivesResponseI {
  incentives: Array<IncentiveSummaryI>;
}

// A 404 is the switch being off: an answer, not an error to log on every
// load. Built per call: the request interceptor writes into `meta`.
const switchProbeConfig = () =>
  ({
    meta: { expectedStatuses: [404] },
  }) as AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI;

const incentivePath = (id: string) =>
  `/v1/manage/incentives/${encodeURIComponent(id)}`;

const workspacePath = (workspaceId: string) =>
  `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/incentives`;

const periodParams = (periodStart?: string | null) =>
  periodStart ? { params: { period_start: periodStart } } : {};

export const IncentivesAPIEndpoints = (api: AxiosInstance) => ({
  /** An LGA's (or an Admin's) incentives on one workspace. */
  listWorkspaceIncentives: (workspaceId: string) =>
    api.get<IncentivesResponseI>(
      workspacePath(workspaceId),
      switchProbeConfig()
    ),

  /** A workspace's campaigns and rostered reps, for the builder's pickers. */
  getIncentiveOptions: (workspaceId: string) =>
    api.get<IncentiveOptionsI>(`${workspacePath(workspaceId)}/options`),

  /** Admins: every incentive, all-workspace ones included. */
  listAllIncentives: () =>
    api.get<IncentivesResponseI>("/v1/manage/incentives", switchProbeConfig()),

  getIncentive: (id: string) => api.get<IncentiveResponseI>(incentivePath(id)),

  createIncentive: (body: IncentiveInputI) =>
    api.post<IncentiveResponseI>("/v1/manage/incentives", body),

  updateIncentive: (id: string, body: IncentiveInputI) =>
    api.put<IncentiveResponseI>(incentivePath(id), body),

  setIncentiveStatus: (id: string, status: IncentiveStatusT) =>
    api.post<IncentiveResponseI>(`${incentivePath(id)}/status`, { status }),

  duplicateIncentive: (id: string) =>
    api.post<IncentiveResponseI>(`${incentivePath(id)}/duplicate`),

  deleteIncentive: (id: string) => api.delete(incentivePath(id)),

  getStandings: (id: string, periodStart?: string | null) =>
    api.get<IncentiveStandingsI>(
      `${incentivePath(id)}/standings`,
      periodParams(periodStart)
    ),

  /** The builder's dry run: nothing is saved. */
  previewIncentive: (body: IncentiveInputI, periodStart?: string | null) =>
    api.post<IncentiveStandingsI>("/v1/manage/incentives/preview", {
      ...body,
      period_start: periodStart ?? null,
    }),

  /** A workspace's earnings for an ET month (YYYY-MM): the payout history. */
  getWorkspaceEarnings: (workspaceId: string, month?: string) =>
    api.get<IncentiveEarningsI>(`${workspacePath(workspaceId)}/earnings`, {
      params: month ? { month } : {},
    }),

  /** Admins: every workspace's earnings for an ET month. */
  getAllEarnings: (month?: string) =>
    api.get<IncentiveEarningsI>("/v1/manage/incentives/earnings", {
      params: month ? { month } : {},
    }),
});

export const RewardsAPIEndpoints = (api: AxiosInstance) => ({
  /** The caller's rewards; the nav's switch probe too. */
  getRewards: () => api.get<RewardsI>("/v1/rewards", switchProbeConfig()),

  getReward: (id: string, periodStart?: string | null) =>
    api.get<RewardDetailI>(
      `/v1/rewards/${encodeURIComponent(id)}`,
      periodParams(periodStart)
    ),

  /** Running incentives to announce now (What's new cards). */
  getRewardAnnouncements: () =>
    api.get<RewardAnnouncementsI>(
      "/v1/rewards/announcements",
      switchProbeConfig()
    ),
});
